import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Trash2, Plus, UserPlus } from "lucide-react";

const KINDS = [
  { key: "scripts", label: "Scripts", fields: ["title", "category", "body"] },
  { key: "tools", label: "Tools", fields: ["name", "category", "url", "cost", "purpose", "order"] },
  { key: "tiers", label: "Tiers", fields: ["name", "split", "requirement", "order"] },
  { key: "compliance", label: "Compliance", fields: ["title", "category", "summary"] },
  { key: "glossary", label: "Glossary", fields: ["term", "definition"] },
  { key: "listing_check", label: "Listing Checks", fields: ["title", "phase", "order"] },
  { key: "buyer_steps", label: "Buyer Steps", fields: ["title", "phase", "order"] },
];

export default function Admin() {
  return (
    <div className="space-y-7" data-testid="admin-page">
      <header>
        <div className="overline">Admin · Broker Only</div>
        <h1 className="font-heading text-5xl mt-2">Back Office</h1>
        <p className="text-sm text-[#050303]/60 mt-2">Add, edit, delete content across the entire command center.</p>
      </header>

      <Tabs defaultValue="agents">
        <TabsList className="bg-[#ffffff] border border-[#d1cece]">
          <TabsTrigger value="agents" data-testid="tab-agents">Agents</TabsTrigger>
          {KINDS.map(k => <TabsTrigger key={k.key} value={k.key} data-testid={`tab-${k.key}`}>{k.label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="agents"><AgentsPanel /></TabsContent>
        {KINDS.map(k => <TabsContent key={k.key} value={k.key}><ContentPanel kind={k.key} fields={k.fields} /></TabsContent>)}
      </Tabs>
    </div>
  );
}

function AgentsPanel() {
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "agent" });
  const load = () => api.get("/auth/agents").then(r => setAgents(r.data || []));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/register", form);
      toast.success("Agent created");
      setForm({ email: "", password: "", name: "", role: "agent" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this agent?")) return;
    await api.delete(`/auth/agents/${id}`);
    load();
  };

  const toggleOnDuty = async (id, next) => {
    // Optimistic — flip locally then confirm with the server
    setAgents(list => list.map(a => a.id === id ? { ...a, on_duty: next } : a));
    try {
      await api.patch(`/auth/agents/${id}/on-duty`, { on_duty: next });
      toast.success(next ? "On duty" : "Off duty");
    } catch {
      toast.error("Toggle failed");
      load();
    }
  };

  const agentRoster = agents.filter(a => a.role === "agent");
  const onDutyCount = agentRoster.filter(a => a.on_duty).length;

  return (
    <div className="grid lg:grid-cols-3 gap-6 mt-6">
      <form onSubmit={create} className="core-card p-6 space-y-3" data-testid="new-agent-form">
        <h3 className="font-heading text-xl">New Agent</h3>
        <input required placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-[#d1cece] rounded-md" data-testid="agent-name" />
        <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-[#d1cece] rounded-md" data-testid="agent-email" />
        <input required type="password" placeholder="Password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2 border border-[#d1cece] rounded-md" data-testid="agent-password" />
        <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border border-[#d1cece] rounded-md">
          <option value="agent">Agent</option><option value="admin">Admin</option>
        </select>
        <button type="submit" className="w-full bg-[#050303] text-white pill-btn flex items-center justify-center gap-2" data-testid="create-agent-btn"><UserPlus size={15} /> Create</button>
      </form>
      <div className="lg:col-span-2 core-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl">Active Roster</h3>
          <div className="text-[11px] text-[#050303]/60" data-testid="on-duty-count">
            <span className="font-semibold text-[#bf858e]">{onDutyCount}</span> of {agentRoster.length} on duty · new /clients leads auto-route to on-duty agents
          </div>
        </div>
        <div className="space-y-2">
          {agents.map(a => (
            <div key={a.id} className="flex items-center justify-between border-b border-[#d1cece] pb-2 gap-3" data-testid={`agent-row-${a.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{a.name}</div>
                <div className="text-xs text-[#050303]/55 truncate">{a.email} · <span className="capitalize">{a.role}</span></div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {a.role === "agent" && (
                  <label
                    className="flex items-center gap-2 cursor-pointer select-none"
                    title="Toggle to receive round-robin /clients leads"
                    data-testid={`on-duty-toggle-${a.id}`}
                  >
                    <span className={`text-[10px] uppercase tracking-widest ${a.on_duty ? "text-[#bf858e]" : "text-[#050303]/40"}`}>
                      {a.on_duty ? "On Duty" : "Off Duty"}
                    </span>
                    <span
                      className={`relative w-9 h-5 rounded-full transition-colors ${a.on_duty ? "bg-[#bf858e]" : "bg-[#d1cece]"}`}
                      onClick={(e) => { e.preventDefault(); toggleOnDuty(a.id, !a.on_duty); }}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${a.on_duty ? "translate-x-4" : ""}`}
                      />
                    </span>
                  </label>
                )}
                {a.role === "agent" && (
                  <button onClick={() => del(a.id)} className="text-[#050303]/40 hover:text-[#bf858e]" data-testid={`delete-agent-${a.id}`}><Trash2 size={15} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FieldInput({ field, value, onChange, kind, prefix = "" }) {
  const isLong = field === "body" || field === "definition" || field === "summary" || field === "purpose";
  const testId = `${prefix}${kind}-${field}`;
  const cls = "w-full px-3 py-2 border border-[#d1cece] rounded-md";
  if (isLong) {
    return <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testId} />;
  }
  return <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testId} />;
}

function ContentRow({ kind, fields, item, editing, editForm, onStartEdit, onSaveEdit, onCancelEdit, onChangeField, onDelete }) {
  if (editing) {
    return (
      <div className="border-b border-[#d1cece] pb-3 space-y-2" data-testid={`${kind}-row-${item.id}`}>
        {fields.map((f) => (
          <div key={f}>
            <label className="text-[10px] uppercase tracking-wider text-[#050303]/55">{f}</label>
            <FieldInput field={f} value={editForm[f]} onChange={(v) => onChangeField(f, v)} kind={kind} prefix="edit-" />
          </div>
        ))}
        <div className="flex gap-2">
          <button onClick={onSaveEdit} className="bg-[#050303] text-white pill-btn text-xs" data-testid={`save-${kind}-${item.id}`}>Save</button>
          <button onClick={onCancelEdit} className="border border-[#050303] pill-btn text-xs" data-testid={`cancel-edit-${kind}-${item.id}`}>Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div className="border-b border-[#d1cece] pb-3 flex items-start justify-between gap-3" data-testid={`${kind}-row-${item.id}`}>
      <div className="text-sm min-w-0">
        <div className="font-semibold truncate">{item.title || item.name || item.term || item.id}</div>
        <div className="text-xs text-[#050303]/55 truncate">{item.category || item.phase || item.split || item.cost || ""}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={onStartEdit} className="text-[#050303]/55 hover:text-[#bf858e] text-xs uppercase tracking-wider" data-testid={`edit-${kind}-${item.id}`}>Edit</button>
        <button onClick={onDelete} className="text-[#050303]/40 hover:text-[#bf858e]" data-testid={`delete-${kind}-${item.id}`}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function ContentPanel({ kind, fields }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = () => api.get(`/content/${kind}`).then((r) => setItems(r.data || []));
  useEffect(() => {
    load();
    setForm({});
    setEditId(null);
  }, [kind]);

  const create = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (payload.order) payload.order = parseInt(payload.order);
    if (kind === "scripts") payload.id = (payload.title || "x").toLowerCase().replace(/\s+/g, "-").slice(0, 32);
    await api.post(`/content/${kind}`, payload);
    toast.success("Added");
    setForm({});
    load();
  };

  const saveEdit = async () => {
    const payload = { ...editForm };
    if (payload.order) payload.order = parseInt(payload.order);
    await api.put(`/content/${kind}/${editId}`, payload);
    toast.success("Saved");
    setEditId(null);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/content/${kind}/${id}`);
    load();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6 mt-6">
      <form onSubmit={create} className="core-card p-6 space-y-3" data-testid={`new-${kind}-form`}>
        <h3 className="font-heading text-xl flex items-center gap-2"><Plus size={16} /> New {kind}</h3>
        {fields.map((f) => (
          <div key={f}>
            <label className="text-xs uppercase tracking-wider text-[#050303]/60">{f}</label>
            <FieldInput field={f} value={form[f]} onChange={(v) => setForm({ ...form, [f]: v })} kind={kind} />
          </div>
        ))}
        <button type="submit" className="w-full bg-[#050303] text-white pill-btn" data-testid={`create-${kind}-btn`}>Create</button>
      </form>
      <div className="lg:col-span-2 core-card p-6">
        <h3 className="font-heading text-xl mb-4">Existing {kind}</h3>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {items.map((it) => (
            <ContentRow
              key={it.id}
              kind={kind}
              fields={fields}
              item={it}
              editing={editId === it.id}
              editForm={editForm}
              onStartEdit={() => { setEditId(it.id); setEditForm({ ...it }); }}
              onSaveEdit={saveEdit}
              onCancelEdit={() => setEditId(null)}
              onChangeField={(f, v) => setEditForm({ ...editForm, [f]: v })}
              onDelete={() => del(it.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
